import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Check, 
  Clock, 
  Rocket,
  ChevronDown,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Release Notes Admin Page
 * 
 * Verwaltet versionierte Änderungslisten.
 * - Versionen erstellen/bearbeiten/löschen
 * - Einträge zu Versionen hinzufügen
 * - Versionen veröffentlichen (Draft -> Live)
 * 
 * Nur für Admins zugänglich.
 */
const ReleaseNotesPage = () => {
  const { API } = useContext(AuthContext);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersions, setExpandedVersions] = useState({});
  const [versionEntries, setVersionEntries] = useState({});
  
  // Dialoge
  const [showCreateVersionDialog, setShowCreateVersionDialog] = useState(false);
  const [showEditVersionDialog, setShowEditVersionDialog] = useState(false);
  const [showCreateEntryDialog, setShowCreateEntryDialog] = useState(false);
  const [showEditEntryDialog, setShowEditEntryDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  
  // Form Data
  const [newVersionNumber, setNewVersionNumber] = useState('');
  const [newVersionNotes, setNewVersionNotes] = useState('');
  const [editingVersion, setEditingVersion] = useState(null);
  const [newEntryDescription, setNewEntryDescription] = useState('');
  const [newEntryVersionId, setNewEntryVersionId] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/release-notes/versions`);
      setVersions(response.data.versions || []);
      
      // Draft-Versionen automatisch expandieren
      const expanded = {};
      (response.data.versions || []).forEach(v => {
        if (v.status === 'draft') {
          expanded[v.id] = true;
        }
      });
      setExpandedVersions(expanded);
      
      // Einträge für expandierte Versionen laden
      for (const v of response.data.versions || []) {
        if (v.status === 'draft') {
          await fetchEntriesForVersion(v.id);
        }
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Versionen');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntriesForVersion = async (versionId) => {
    try {
      const response = await axios.get(`${API}/release-notes/versions/${versionId}`);
      setVersionEntries(prev => ({
        ...prev,
        [versionId]: response.data.entries || []
      }));
    } catch (error) {
      console.error('Fehler beim Laden der Einträge:', error);
    }
  };

  const toggleVersion = async (versionId) => {
    const isExpanded = expandedVersions[versionId];
    setExpandedVersions(prev => ({
      ...prev,
      [versionId]: !isExpanded
    }));
    
    if (!isExpanded && !versionEntries[versionId]) {
      await fetchEntriesForVersion(versionId);
    }
  };

  // Version erstellen
  const handleCreateVersion = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/release-notes/versions`, {
        version_number: newVersionNumber,
        notes: newVersionNotes
      });
      toast.success(`Version ${newVersionNumber} erstellt`);
      setShowCreateVersionDialog(false);
      setNewVersionNumber('');
      setNewVersionNotes('');
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  // Version bearbeiten
  const handleEditVersion = async (e) => {
    e.preventDefault();
    if (!editingVersion) return;
    
    try {
      await axios.put(`${API}/release-notes/versions/${editingVersion.id}`, {
        version_number: editingVersion.version_number,
        notes: editingVersion.notes
      });
      toast.success('Version aktualisiert');
      setShowEditVersionDialog(false);
      setEditingVersion(null);
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // Version veröffentlichen
  const handlePublishVersion = async () => {
    if (!publishTarget) return;
    
    try {
      await axios.put(`${API}/release-notes/versions/${publishTarget.id}`, {
        status: 'live'
      });
      toast.success(`Version ${publishTarget.version_number} veröffentlicht!`);
      setShowPublishDialog(false);
      setPublishTarget(null);
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Veröffentlichen');
    }
  };

  // Version/Eintrag löschen
  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteTarget.type === 'version') {
        await axios.delete(`${API}/release-notes/versions/${deleteTarget.id}`);
        toast.success(`Version ${deleteTarget.version_number} gelöscht`);
      } else {
        await axios.delete(`${API}/release-notes/entries/${deleteTarget.id}`);
        toast.success('Eintrag gelöscht');
      }
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Eintrag erstellen
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/release-notes/entries`, {
        description: newEntryDescription,
        version_id: newEntryVersionId || undefined
      });
      toast.success('Eintrag hinzugefügt');
      setShowCreateEntryDialog(false);
      setNewEntryDescription('');
      setNewEntryVersionId('');
      fetchVersions();
      // Einträge der betroffenen Version neu laden
      if (newEntryVersionId) {
        await fetchEntriesForVersion(newEntryVersionId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  // Eintrag bearbeiten
  const handleEditEntry = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;
    
    try {
      await axios.put(`${API}/release-notes/entries/${editingEntry.id}`, {
        description: editingEntry.description,
        version_id: editingEntry.version_id
      });
      toast.success('Eintrag aktualisiert');
      setShowEditEntryDialog(false);
      setEditingEntry(null);
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // Hilfsfunktion für Draft-Versionen
  const draftVersions = versions.filter(v => v.status === 'draft');
  const liveVersions = versions.filter(v => v.status === 'live');

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="release-notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Versionen & Release Notes
          </h1>
          <p className="text-slate-600">Änderungen dokumentieren und Versionen verwalten</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setNewEntryVersionId(draftVersions[0]?.id || '');
              setShowCreateEntryDialog(true);
            }}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="add-entry-button"
          >
            <Plus size={18} />
            Eintrag hinzufügen
          </Button>
          <Button
            onClick={() => setShowCreateVersionDialog(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600"
            data-testid="create-version-button"
          >
            <Plus size={18} />
            Neue Version
          </Button>
        </div>
      </div>

      {/* Draft Versionen */}
      {draftVersions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2 text-amber-700">
            <Clock size={20} />
            Entwürfe
          </h2>
          {draftVersions.map(version => (
            <Card key={version.id} className="overflow-hidden border-amber-200 border-2" data-testid={`version-card-${version.version_number}`}>
              {/* Version Header */}
              <div 
                className="p-4 bg-amber-50 flex items-center justify-between cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => toggleVersion(version.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedVersions[version.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <div>
                    <h3 className="font-bold text-lg">Version {version.version_number}</h3>
                    <span className="text-sm text-amber-600 flex items-center gap-1">
                      <Clock size={14} />
                      Entwurf • {version.entry_count} Einträge
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingVersion({...version});
                      setShowEditVersionDialog(true);
                    }}
                    data-testid={`edit-version-${version.version_number}`}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setDeleteTarget({...version, type: 'version'});
                      setShowDeleteDialog(true);
                    }}
                    data-testid={`delete-version-${version.version_number}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      setPublishTarget(version);
                      setShowPublishDialog(true);
                    }}
                    data-testid={`publish-version-${version.version_number}`}
                  >
                    <Rocket size={16} className="mr-1" />
                    Veröffentlichen
                  </Button>
                </div>
              </div>

              {/* Einträge */}
              {expandedVersions[version.id] && (
                <div className="p-4 border-t border-amber-200">
                  {version.notes && (
                    <p className="text-sm text-slate-600 mb-4 italic">{version.notes}</p>
                  )}
                  
                  {versionEntries[version.id]?.length > 0 ? (
                    <ul className="space-y-2">
                      {versionEntries[version.id].map(entry => (
                        <li key={entry.id} className="flex items-start justify-between group p-2 rounded hover:bg-slate-50">
                          <div className="flex items-start gap-2">
                            <span className="text-amber-600 mt-1">•</span>
                            <span>{entry.description}</span>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingEntry({...entry});
                                setShowEditEntryDialog(true);
                              }}
                              className="h-7 w-7 p-0"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDeleteTarget({...entry, type: 'entry'});
                                setShowDeleteDialog(true);
                              }}
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-500 text-center py-4">
                      Noch keine Einträge. Füge Änderungen hinzu!
                    </p>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      setNewEntryVersionId(version.id);
                      setShowCreateEntryDialog(true);
                    }}
                  >
                    <Plus size={16} className="mr-1" />
                    Eintrag hinzufügen
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Live Versionen */}
      {liveVersions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2 text-green-700">
            <Check size={20} />
            Veröffentlicht
          </h2>
          {liveVersions.map(version => (
            <Card key={version.id} className="overflow-hidden" data-testid={`version-card-${version.version_number}`}>
              {/* Version Header */}
              <div 
                className="p-4 bg-green-50 flex items-center justify-between cursor-pointer hover:bg-green-100 transition-colors"
                onClick={() => toggleVersion(version.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedVersions[version.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <div>
                    <h3 className="font-bold text-lg">Version {version.version_number}</h3>
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <Check size={14} />
                      Live {version.published_at && `• ${format(new Date(version.published_at), 'dd.MM.yyyy HH:mm', { locale: de })}`}
                      {' '}• {version.entry_count} Einträge
                    </span>
                  </div>
                </div>
              </div>

              {/* Einträge */}
              {expandedVersions[version.id] && (
                <div className="p-4 border-t border-green-200">
                  {version.notes && (
                    <p className="text-sm text-slate-600 mb-4 italic">{version.notes}</p>
                  )}
                  
                  {versionEntries[version.id]?.length > 0 ? (
                    <ul className="space-y-2">
                      {versionEntries[version.id].map(entry => (
                        <li key={entry.id} className="flex items-start gap-2 p-2">
                          <span className="text-green-600 mt-1">•</span>
                          <span>{entry.description}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Keine Einträge</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Keine Versionen */}
      {versions.length === 0 && (
        <Card className="p-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">
            Noch keine Versionen vorhanden
          </h3>
          <p className="text-slate-500 mb-4">
            Erstellen Sie Ihre erste Version, um Änderungen zu dokumentieren.
          </p>
          <Button onClick={() => setShowCreateVersionDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Erste Version anlegen
          </Button>
        </Card>
      )}

      {/* Dialog: Neue Version */}
      <Dialog open={showCreateVersionDialog} onOpenChange={setShowCreateVersionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Version erstellen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateVersion} className="space-y-4">
            <div>
              <Label>Versionsnummer</Label>
              <Input
                value={newVersionNumber}
                onChange={(e) => setNewVersionNumber(e.target.value)}
                placeholder="z.B. 1.6"
                required
                data-testid="version-number-input"
              />
            </div>
            <div>
              <Label>Beschreibung (optional)</Label>
              <Textarea
                value={newVersionNotes}
                onChange={(e) => setNewVersionNotes(e.target.value)}
                placeholder="Kurze Beschreibung der Version..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateVersionDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="submit-create-version">Erstellen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Version bearbeiten */}
      <Dialog open={showEditVersionDialog} onOpenChange={setShowEditVersionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Version bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditVersion} className="space-y-4">
            <div>
              <Label>Versionsnummer</Label>
              <Input
                value={editingVersion?.version_number || ''}
                onChange={(e) => setEditingVersion(prev => ({...prev, version_number: e.target.value}))}
                required
              />
            </div>
            <div>
              <Label>Beschreibung (optional)</Label>
              <Textarea
                value={editingVersion?.notes || ''}
                onChange={(e) => setEditingVersion(prev => ({...prev, notes: e.target.value}))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditVersionDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit">Speichern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Neuer Eintrag */}
      <Dialog open={showCreateEntryDialog} onOpenChange={setShowCreateEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Änderung hinzufügen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEntry} className="space-y-4">
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={newEntryDescription}
                onChange={(e) => setNewEntryDescription(e.target.value)}
                placeholder="Kurze, verständliche Beschreibung der Änderung..."
                required
                rows={3}
                data-testid="entry-description-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Nutzermehrwert beschreiben, keine technischen Details
              </p>
            </div>
            <div>
              <Label>Version</Label>
              <Select value={newEntryVersionId} onValueChange={setNewEntryVersionId}>
                <SelectTrigger data-testid="entry-version-select">
                  <SelectValue placeholder="Version auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {draftVersions.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.version_number} (Entwurf)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Nur Draft-Versionen können bearbeitet werden
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateEntryDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="submit-create-entry">Hinzufügen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Eintrag bearbeiten */}
      <Dialog open={showEditEntryDialog} onOpenChange={setShowEditEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eintrag bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditEntry} className="space-y-4">
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={editingEntry?.description || ''}
                onChange={(e) => setEditingEntry(prev => ({...prev, description: e.target.value}))}
                required
                rows={3}
              />
            </div>
            <div>
              <Label>Version</Label>
              <Select 
                value={editingEntry?.version_id || ''} 
                onValueChange={(val) => setEditingEntry(prev => ({...prev, version_id: val}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Version auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {draftVersions.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.version_number} (Entwurf)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditEntryDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit">Speichern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Löschen bestätigen */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              {deleteTarget?.type === 'version' ? 'Version löschen' : 'Eintrag löschen'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            {deleteTarget?.type === 'version' 
              ? `Möchten Sie Version ${deleteTarget?.version_number} wirklich löschen? Alle zugehörigen Einträge werden ebenfalls gelöscht.`
              : 'Möchten Sie diesen Eintrag wirklich löschen?'
            }
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Veröffentlichen bestätigen */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Rocket size={20} />
              Version veröffentlichen
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Möchten Sie Version <strong>{publishTarget?.version_number}</strong> wirklich veröffentlichen?
            <br /><br />
            Nach der Veröffentlichung kann die Version nicht mehr gelöscht werden.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700" 
              onClick={handlePublishVersion}
              data-testid="confirm-publish"
            >
              <Rocket size={16} className="mr-1" />
              Veröffentlichen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReleaseNotesPage;
